import { useQuery, useMutation } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Grid, Paper, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { EssealDataTable } from 'esseal-data-table';
import { find } from 'lodash';
import React, { useContext, useState } from 'react';

import ConflictDetectedModal from './ConflictDetectedModal';
import peopleRegisterColumns from './peopleRegisterColumns';
import PeopleRegisterToolbar from './Toolbar';

import EditScheduleModal from '@/components/Scheduling/EditScheduleModal';
import NewScheduleModal from '@/components/Scheduling/NewScheduleModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  GetSchedulesDocument,
  CompanyResourceResourceTypeEnum,
  UpdateScheduleDocument,
  PermissionActionEnum,
  PermissionEntityEnum,
  ResourceScheduleStatusEnum,
  type Schedule,
  EmploymentStatusEnum,
  type CreateSingleScheduleMutation,
} from '@/graphql/generated/graphql';
import { StyledRegisterHead } from '@/theme/styled-components';
import { displayNotification } from '@/utils/notifications';

const PeopleRegister: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedProject, selectedReport, projectShifts, projectHolidays } = useContext(ReportContext);
  const { can } = useContext(PermissionContext);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [conflictingId, setConflictingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, loading } = useQuery(GetSchedulesDocument, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !selectedReport,
    variables: {
      date: selectedReport?.reportDate as string,
      projectId: selectedProject?.id as string,
      resourceType: CompanyResourceResourceTypeEnum.Human,
    },
  });

  const [update] = useMutation(UpdateScheduleDocument);

  const handleAddFunction = () => {
    if (!can(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setOpenScheduleModal(true);
  };

  const handleEditFunction = (id: string) => {
    if (!can(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;

    setSelectedId(id);
  };

  const handleDeleteFunction = (id: string) => {
    if (!can(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling, { projectId: selectedProject?.id })) return;
    if (!selectedReport || !selectedProject) return;

    update({
      variables: {
        schedule: {
          id,
          status: ResourceScheduleStatusEnum.Dropped,
        },
      },
      refetchQueries: [
        {
          query: GetSchedulesDocument,
          variables: {
            projectId: selectedProject.id,
            date: selectedReport.reportDate,
            resourceType: CompanyResourceResourceTypeEnum.Human,
          },
        },
      ],
      onCompleted: () =>
        displayNotification({
          type: 'success',
          text: 'People schedule removed successfully',
        }),
      onError: (error) => displayNotification({ type: 'error', text: error.message }),
    });
  };

  if (!selectedReport) return <></>;

  const selectedSchedule = find(data?.getSchedules, (schedule) => schedule.id === selectedId);

  return (
    <Grid
      component={Paper}
      mb={2}
      size={12}
      sx={{
        '.scheduled-row': {
          '.dg-cell': {
            backgroundColor: red[50],
            borderColor: red[100],
          },
          '&:hover': {
            '.dg-cell': {
              backgroundColor: red[100],
            },
          },
        },
      }}
    >
      <StyledRegisterHead>
        <Typography variant="caption" fontWeight={700}>
          People Register
        </Typography>
      </StyledRegisterHead>
      <EssealDataTable
        height={320}
        rows={(data?.getSchedules as Schedule[]) || []}
        columns={peopleRegisterColumns(dateTimePreference.time as string)}
        loading={loading}
        maxVisibleActions={2}
        toolbar={<PeopleRegisterToolbar onAddRecord={handleAddFunction} />}
        rowActions={() => [
          {
            label: 'Edit',
            icon: <EditIcon fontSize="small" />,
            tooltip: 'Edit Schedule',
            onClick: (row) => handleEditFunction(row.id),
          },
          {
            label: 'Delete',
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Delete Schedule',
            onClick: (row) => handleDeleteFunction(row.id),
          },
        ]}
        getRowClassName={(row) =>
          row.companyResource?.user?.defaultEmploymentStatus === EmploymentStatusEnum.Pending ? 'scheduled-row' : ''
        }
      />

      {openScheduleModal && (
        <NewScheduleModal
          addresses={selectedProject?.addresses || []}
          resourceType={CompanyResourceResourceTypeEnum.Human}
          reportDate={selectedReport.reportDate}
          projectId={selectedProject?.id as string}
          reportId={selectedReport?.id as string}
          projectShifts={projectShifts}
          projectHolidays={projectHolidays}
          handleClose={() => setOpenScheduleModal(false)}
          handleSuccess={(response: CreateSingleScheduleMutation) => {
            if (response.createSingleSchedule.hasConflicts) {
              setConflictingId(response.createSingleSchedule.scheduleId);
            }
          }}
        />
      )}

      {selectedId && selectedSchedule && (
        <EditScheduleModal handleClose={() => setSelectedId(null)} selectedSchedule={selectedSchedule} />
      )}

      {conflictingId && (
        <ConflictDetectedModal
          conflictingId={conflictingId}
          handleClose={() => setConflictingId(null)}
          handleCancel={() => {
            setConflictingId(null);
            setOpenScheduleModal(true);
          }}
        />
      )}
    </Grid>
  );
};

export default PeopleRegister;
